import type React from "react";
import {
	Box,
	Button,
	Grid,
	Modal as MUIModal,
	styled,
	type Breakpoint,
	type ButtonProps,
	type ModalProps as MUIModalProps,
	type SvgIconProps,
	type SxProps,
} from "@mui/material";

import { ModalContainer, ModalContentContainer, ModalTitleContainer } from "../components";

const StyledModalContentContainer = styled(ModalContentContainer)(({ theme }) => ({
	paddingTop: 0,
	paddingBottom: 0,
	paddingLeft: theme.spacing(3),
	paddingRight: theme.spacing(3),
}));

const StyledIconBox = styled(Box)(({ theme }) => ({
	backgroundColor: theme.darken(theme.palette.background.paper, 0.04),
	padding: theme.spacing(1),
	borderRadius: theme.shape.borderRadius,

	lineHeight: 0,
}));

interface AlertModalSlotProps {
	contentContainer?: {
		sx: SxProps;
	};
	icon?: {
		sx: SxProps;
	};
	confirmButton?: ButtonProps & {
		"data-testid"?: string;
	};
}

export interface AlertModalProps extends Omit<MUIModalProps, "children" | "maxWidth" | "title"> {
	readonly children: React.ReactNode;
	readonly Icon: React.ComponentType<SvgIconProps>;
	readonly title: React.ReactNode | string;
	readonly confirmText: string;
	readonly size?: Breakpoint;
	readonly slotProps?: MUIModalProps["slotProps"] & AlertModalSlotProps;
	onClose(e: React.UIEvent | object | undefined, reason?: "backdropClick" | "escapeKeyDown"): void;
	onConfirm(e: React.UIEvent | undefined): void | Promise<void>;
}

export const AlertModal: React.FC<AlertModalProps> = ({
	title,
	confirmText,
	children,
	size,
	sx,
	slotProps,
	Icon,
	onConfirm,
	onClose,
	...props
}) => {
	const { contentContainer, icon, ...modalSlotProps } = slotProps || {};

	return (
		<MUIModal slotProps={modalSlotProps} onClose={onClose} {...props}>
			<ModalContainer disableGutters maxWidth={size || "xs"} sx={sx}>
				<Grid container direction="column" gap={2} alignItems="center" sx={{ padding: 3 }}>
					<StyledIconBox>
						<Icon sx={{ fontSize: 36, ...icon?.sx }} />
					</StyledIconBox>

					<ModalTitleContainer sx={{ maxWidth: "100%" }}>{title}</ModalTitleContainer>
				</Grid>

				<StyledModalContentContainer sx={contentContainer?.sx}>{children}</StyledModalContentContainer>

				<Box sx={{ padding: 3 }}>
					<Button variant="contained" onClick={onConfirm} fullWidth {...slotProps?.confirmButton}>
						{confirmText}
					</Button>
				</Box>
			</ModalContainer>
		</MUIModal>
	);
};
